// Provenance: repo:tds-reconciliation-portal:frontend/src/components/ReconProgressMeter.tsx
// (ADR-0005) — restyled on the estate tokens (ADR-0010). The canonical tri-state definition
// and the compact strip geometry carry; run ids became UUID strings.
import type { CSSProperties } from 'react'

import { useReconciliation } from '../api/hooks'
import { Money } from './Money'

/** Honest reconciliation progress: how many actionable parties are reconciled
 *  (tie or every difference matched/explained-and-approved) vs pending approval
 *  vs still open — over the ONE canonical definition. Counts both matches and
 *  explanations, unlike the old resolution-only bar.
 *
 *  `compact` (density pass A3) folds the banner into one slim strip for the
 *  title row: a mini bar plus the counts as small text. Progress is status,
 *  not work — status earns a line, not a banner. Every figure the banner
 *  carried is still here (the segment tooltips too). */
export function ReconProgressMeter({ runId, compact = false }: {
  runId: string | null
  compact?: boolean
}) {
  const q = useReconciliation(runId)
  const d = q.data
  if (!d) return null
  const { counts, actionable, open_exposure, complete } = d
  const pct = (n: number) => (actionable ? (n / actionable) * 100 : 0)

  const bar = (h: string, style?: CSSProperties) => (
    <div
      className="flex overflow-hidden rounded bg-muted"
      style={{ height: h, ...style }}
      role="progressbar"
      aria-label="Reconciliation progress"
    >
      <div
        className="bg-emerald-500"
        style={{ width: `${pct(counts.reconciled)}%` }}
        title={`${counts.reconciled} reconciled`}
      />
      <div
        className="bg-amber-400"
        style={{ width: `${pct(counts.pending)}%` }}
        title={`${counts.pending} awaiting reviewer approval`}
      />
      <div
        className="bg-transparent"
        style={{ width: `${pct(counts.open)}%` }}
        title={`${counts.open} open`}
      />
    </div>
  )

  if (compact) {
    return (
      // flex:1 — the bar stretches into whatever room the row leaves it: the
      // strip fills the gap up to the buttons, carrying the counts and
      // exposure rightward with it
      <div className="flex min-w-0 flex-1 items-center gap-2">
        {bar('8px', { flex: 1, minWidth: 120 })}
        <span className="whitespace-nowrap text-sm font-semibold">
          {complete ? (
            <span className="text-emerald-700">All parties reconciled</span>
          ) : (
            <>
              <span className="text-emerald-700">{counts.reconciled} ✓</span>
              {' · '}
              <span className="text-amber-700">{counts.pending} pending</span>
              {' · '}
              <span className="text-muted-foreground">{counts.open} open</span>
            </>
          )}
        </span>
        <span className="whitespace-nowrap text-sm text-muted-foreground">
          exposure <Money v={open_exposure} bold />
        </span>
      </div>
    )
  }

  return (
    <div className="rounded-lg border border-border p-4">
      <div className="mb-1.5 flex items-center justify-between gap-4">
        <p className="font-semibold text-foreground">Reconciliation progress</p>
        <p className={`text-sm font-semibold ${complete ? 'text-emerald-700' : 'text-muted-foreground'}`}>
          {complete
            ? 'All parties reconciled'
            : `${counts.open + counts.pending} of ${actionable} parties still to clear`}
        </p>
      </div>
      {bar('16px')}
      <div className="mt-2 flex flex-wrap items-center gap-4 text-xs">
        <span className="text-emerald-700">● {counts.reconciled} reconciled</span>
        <span className="text-amber-700">● {counts.pending} pending approval</span>
        <span className="text-muted-foreground">● {counts.open} open</span>
        <span className="ml-auto text-muted-foreground">
          Open exposure <Money v={open_exposure} bold className="text-xs" />
        </span>
      </div>
    </div>
  )
}
